import {
  type OpenWorldCoordinate,
  type OpenWorldMapAlly,
  type OpenWorldMapCellState,
  openWorldColumnLabel,
  openWorldCoordinateKey,
  openWorldQuadrantLabel,
  openWorldRowLetter,
} from '@kenocyber/shared';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import type { GameBridge } from '../game/bridge';
import type { OpenWorldSession } from './useOpenWorld';

/**
 * **O mapa dos cem quadrantes, e o minimapa** (gate V28I.R).
 *
 * ## Uma grelha de navegação, e não um radar
 *
 * O que se desenha aqui é **geografia**: onde estão as linhas, as colunas, os
 * oito portos, onde já se esteve e onde estão os companheiros de expedição.
 * Nada de dentro de um quadrante — nem inimigos, nem projéteis, nem recursos,
 * nem caixas, nem a posição exata de uma nave, a própria incluída. Um marcador
 * numa célula diz "para ali", e nunca "ali".
 *
 * Isso não é uma escolha de desenho: o conteúdo secreto **não chega ao
 * navegador**. Não há aqui nada a filtrar, e nada que um CSS pudesse revelar.
 *
 * ## Visitado é um facto sobre o piloto
 *
 * Uma célula visitada diz que este piloto lá esteve. Não diz o que lá havia, não
 * diz quanto valia e não diz o que lá está agora. Duas contas que passaram por
 * `F/7` veem exatamente a mesma célula.
 *
 * ## DOM, e não um segundo canvas
 *
 * Cem células são cem `<button>`. Um segundo contexto WebGL por causa de uma
 * grelha seria pagar um canvas inteiro para desenhar quadrados — e perder o
 * foco de teclado, o nome acessível e a seleção que o DOM dá de graça.
 *
 * ## O mundo continua a correr
 *
 * Abrir isto não pausa a sala, não pára os outros, não pára o combate, não pára
 * um tempo de espera e não protege ninguém. O que ele faz é largar os comandos
 * locais, pelo caminho normal, para que uma tecla presa não continue a voar
 * enquanto se lê a grelha. O aviso está escrito no painel, em palavras.
 */

/** O que uma célula é. Fechado, e por esta precedência. */
function cellStateOf(
  coordinate: OpenWorldCoordinate,
  current: OpenWorldCoordinate | null,
  ports: ReadonlySet<string>,
  discovered: ReadonlySet<string>,
): OpenWorldMapCellState {
  const key = openWorldCoordinateKey(coordinate);
  if (
    current !== null &&
    current.rowIndex === coordinate.rowIndex &&
    current.columnIndex === coordinate.columnIndex
  ) {
    return 'current';
  }
  if (ports.has(key)) {
    return 'port';
  }
  return discovered.has(key) ? 'visited' : 'unvisited';
}

/**
 * Cada estado, em palavras.
 *
 * Uma tabela fechada sobre a enumeração: um quinto estado não compila sem a
 * frase que o descreve. **A cor é reforço; a palavra é a informação** — e é ela
 * que vai para o nome acessível de cada célula.
 */
const CELL_TEXT: Readonly<Record<OpenWorldMapCellState, string>> = {
  current: 'ATUAL',
  port: 'PORTO SEGURO',
  visited: 'VISITADO',
  unvisited: 'NÃO VISITADO',
};

/** O nome acessível de uma célula: onde é, o que é, e quem lá está. */
function cellLabel(
  coordinate: OpenWorldCoordinate,
  state: OpenWorldMapCellState,
  allies: readonly OpenWorldMapAlly[],
): string {
  const place = `${openWorldQuadrantLabel(coordinate)} — ${CELL_TEXT[state]}`;
  if (allies.length === 0) {
    return place;
  }
  return `${place} — ${allies.map((ally) => ally.name).join(', ')}`;
}

/** Os aliados por quadrante. Um mapa, e não uma varredura por célula. */
function alliesByQuadrant(
  allies: readonly OpenWorldMapAlly[],
): ReadonlyMap<string, readonly OpenWorldMapAlly[]> {
  const grouped = new Map<string, OpenWorldMapAlly[]>();
  for (const ally of allies) {
    const key = openWorldCoordinateKey(ally.quadrant);
    const bucket = grouped.get(key);
    if (bucket === undefined) {
      grouped.set(key, [ally]);
      continue;
    }
    bucket.push(ally);
  }
  return grouped;
}

/**
 * **O minimapa: uma janela de três por três à volta do quadrante atual.**
 *
 * Três por três porque o que ele responde é "por onde saio daqui" — e as saídas
 * de um quadrante são quatro vizinhos. Uma janela maior seria um mapa pequeno,
 * e o mapa já existe atrás de um clique.
 *
 * **Nas bordas, a posição espacial é preservada.** Uma célula que não existe
 * fica ausente no sítio dela, e não é empurrada para fora: um piloto em `A/1`
 * vê o canto do mundo onde ele está, e não uma grelha centrada que mente sobre
 * onde estão as paredes.
 *
 * O norte está escrito, e não desenhado com uma seta: uma seta numa grelha de
 * nove células é um pixel a dizer o que uma palavra diz melhor.
 */
export function OpenWorldMinimap({
  session,
  onOpen,
}: {
  readonly session: OpenWorldSession;
  readonly onOpen: () => void;
}): ReactElement | null {
  const map = session.map;
  /*
   * **Memoizado porque este componente vive dentro do HUD**, e o HUD volta a
   * desenhar-se ao ritmo das tramas autoritativas. Construir dois conjuntos de
   * cem coordenadas vinte vezes por segundo seria trabalho por um mapa que só
   * muda quando alguém atravessa uma fronteira.
   */
  const ports = useMemo(
    () => new Set((map?.ports ?? []).map((port) => openWorldCoordinateKey(port.quadrant))),
    [map?.ports],
  );
  const discovered = useMemo(
    () => new Set((map?.discovered ?? []).map(openWorldCoordinateKey)),
    [map?.discovered],
  );
  const allies = useMemo(() => alliesByQuadrant(map?.allies ?? []), [map?.allies]);
  const current = map?.current ?? null;
  if (map === null || current === null) {
    return null;
  }

  const rows = [-1, 0, 1];
  const columns = [-1, 0, 1];

  return (
    <div className="open-world-minimap" data-testid="open-world-minimap">
      <div className="open-world-minimap__north" aria-hidden="true">
        N
      </div>
      <div className="open-world-minimap__grid">
        {rows.map((rowOffset) =>
          columns.map((columnOffset) => {
            const rowIndex = current.rowIndex + rowOffset;
            const columnIndex = current.columnIndex + columnOffset;
            const outside =
              rowIndex < 0 ||
              columnIndex < 0 ||
              rowIndex >= map.bounds.rows ||
              columnIndex >= map.bounds.columns;
            if (outside) {
              /*
               * A borda do mundo. Uma célula VAZIA no sítio onde ela não existe
               * — e não uma célula deslocada: o buraco é a informação.
               */
              return (
                <span
                  key={`${rowOffset}:${columnOffset}`}
                  className="open-world-minimap__cell is-outside"
                  aria-hidden="true"
                />
              );
            }
            const coordinate = { rowIndex, columnIndex };
            const state = cellStateOf(coordinate, current, ports, discovered);
            const here = allies.get(openWorldCoordinateKey(coordinate)) ?? [];
            return (
              <span
                key={`${rowOffset}:${columnOffset}`}
                className={`open-world-minimap__cell is-${state}`}
                title={cellLabel(coordinate, state, here)}
              >
                <span className="open-world-minimap__label">
                  {openWorldQuadrantLabel(coordinate)}
                </span>
                {here.length > 0 ? (
                  <span className="open-world-minimap__allies">{here.length}</span>
                ) : null}
              </span>
            );
          }),
        )}
      </div>
      <button
        type="button"
        className="open-world-minimap__open"
        data-testid="open-world-minimap-open"
        onClick={onOpen}
      >
        MAPA
      </button>
    </div>
  );
}

/**
 * **O mapa completo: dez por dez, e a legenda escrita.**
 *
 * Selecionar uma célula lê o estado dela. **Não** teleporta, não traça rota, não
 * move a nave, não revela conteúdo, não inicia travessia e não protege ninguém —
 * e nenhuma dessas coisas é impedida por uma verificação: não existe aqui uma
 * chamada que as soubesse fazer.
 */
export function OpenWorldMapPanel({
  session,
  bridge,
  onClose,
}: {
  readonly session: OpenWorldSession;
  readonly bridge: GameBridge;
  readonly onClose: () => void;
}): ReactElement {
  const map = session.map;
  const [selected, setSelected] = useState<OpenWorldCoordinate | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const refreshMap = session.refreshMap;

  /*
   * **Abrir lê, e largar os comandos locais.**
   *
   * A leitura acontece porque uma vista antiga é o que se tem enquanto a nova
   * não chega, e não porque o mapa sonde: fora deste instante ele só volta a ler
   * por eventos que já são conhecidos.
   *
   * Largar os comandos não pausa nada. A sala continua, o combate continua, e a
   * nave fica parada e vulnerável — o que o aviso diz por escrito.
   */
  useEffect(() => {
    void refreshMap();
    bridge.sendToGame('open-world:controls', { enabled: false });
    closeRef.current?.focus();
    return () => {
      /* Ao fechar, os comandos voltam — e voltam LARGADOS, nunca repetidos. */
      bridge.sendToGame('open-world:controls', { enabled: true });
    };
  }, [bridge, refreshMap]);

  const ports = useMemo(
    () => new Set((map?.ports ?? []).map((port) => openWorldCoordinateKey(port.quadrant))),
    [map?.ports],
  );
  const discovered = useMemo(
    () => new Set((map?.discovered ?? []).map(openWorldCoordinateKey)),
    [map?.discovered],
  );
  const allies = useMemo(() => alliesByQuadrant(map?.allies ?? []), [map?.allies]);

  const bounds = map?.bounds ?? { rows: 0, columns: 0 };
  const current = map?.current ?? null;
  const selectedState =
    selected === null ? null : cellStateOf(selected, current, ports, discovered);
  const selectedAllies =
    selected === null ? [] : (allies.get(openWorldCoordinateKey(selected)) ?? []);

  return (
    <div className="open-world-map" data-testid="open-world-map">
      <div className="open-world-map__panel" role="dialog" aria-label="Mapa do mundo aberto">
        <header className="open-world-map__head">
          <h2>MAPA</h2>
          <button
            type="button"
            ref={closeRef}
            className="open-world-map__close"
            data-testid="open-world-map-close"
            onClick={onClose}
          >
            FECHAR
          </button>
        </header>

        {/*
          O aviso é texto, e está em cima: um jogador que abre o mapa a meio de
          um combate tem de o ler antes de olhar para a grelha.
        */}
        <p className="open-world-map__warning" data-testid="open-world-map-warning">
          O MUNDO CONTINUA ATIVO — SUA NAVE PERMANECE VULNERÁVEL.
        </p>

        <div className="open-world-map__scroll">
          <div
            className="open-world-map__grid"
            style={{ ['--open-world-map-columns' as string]: String(bounds.columns) }}
          >
            {/* O canto vazio, e depois os números das colunas. */}
            <span className="open-world-map__corner" aria-hidden="true" />
            {Array.from({ length: bounds.columns }, (_, columnIndex) => (
              <span
                key={openWorldColumnLabel(columnIndex)}
                className="open-world-map__axis"
                aria-hidden="true"
              >
                {openWorldColumnLabel(columnIndex)}
              </span>
            ))}
            {Array.from({ length: bounds.rows }, (_, rowIndex) => (
              <MapRow
                key={openWorldRowLetter(rowIndex)}
                rowIndex={rowIndex}
                columns={bounds.columns}
                current={current}
                ports={ports}
                discovered={discovered}
                allies={allies}
                selected={selected}
                onSelect={setSelected}
              />
            ))}
          </div>
        </div>

        {/*
          A legenda em palavras. Ela existe porque a cor sozinha não é uma
          informação: quem não distingue dois tons lê aqui o que cada uma é.
        */}
        <ul className="open-world-map__legend" data-testid="open-world-map-legend">
          <li>
            <span className="open-world-map__swatch is-current" aria-hidden="true" /> ATUAL — onde
            você está
          </li>
          <li>
            <span className="open-world-map__swatch is-port" aria-hidden="true" /> PORTO SEGURO — um
            dos oito
          </li>
          <li>
            <span className="open-world-map__swatch is-visited" aria-hidden="true" /> VISITADO —
            você já esteve aqui
          </li>
          <li>
            <span className="open-world-map__swatch is-unvisited" aria-hidden="true" /> NÃO VISITADO
          </li>
          <li>
            <span className="open-world-map__swatch is-ally" aria-hidden="true" /> ALIADOS — quantos
            da sua PT estão ali
          </li>
        </ul>

        {/*
          O que a seleção faz, e é tudo: LÊ. Não há aqui um botão de viajar, e
          não há uma chamada que o soubesse fazer.
        */}
        <div className="open-world-map__detail" data-testid="open-world-map-detail">
          {selected === null || selectedState === null ? (
            <p>Selecione um quadrante para ler o estado dele.</p>
          ) : (
            <>
              <p>
                <strong>{openWorldQuadrantLabel(selected)}</strong> — {CELL_TEXT[selectedState]}
              </p>
              {selectedAllies.length > 0 ? (
                <ul className="open-world-map__detail-allies">
                  {selectedAllies.map((ally) => (
                    <li key={ally.id}>
                      {ally.name}
                      {ally.leader ? ' (LÍDER)' : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="open-world-map__detail-note">
                Selecionar não move a nave, não traça rota e não revela o que existe ali.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Uma linha da grelha: a letra, e depois as células.
 *
 * Extraída para que uma linha volte a desenhar-se sozinha quando só ela muda —
 * e para que o rótulo da linha viva ao lado das células a que pertence.
 */
function MapRow({
  rowIndex,
  columns,
  current,
  ports,
  discovered,
  allies,
  selected,
  onSelect,
}: {
  readonly rowIndex: number;
  readonly columns: number;
  readonly current: OpenWorldCoordinate | null;
  readonly ports: ReadonlySet<string>;
  readonly discovered: ReadonlySet<string>;
  readonly allies: ReadonlyMap<string, readonly OpenWorldMapAlly[]>;
  readonly selected: OpenWorldCoordinate | null;
  readonly onSelect: (coordinate: OpenWorldCoordinate) => void;
}): ReactElement {
  return (
    <>
      <span className="open-world-map__axis" aria-hidden="true">
        {openWorldRowLetter(rowIndex)}
      </span>
      {Array.from({ length: columns }, (_, columnIndex) => {
        const coordinate = { rowIndex, columnIndex };
        const state = cellStateOf(coordinate, current, ports, discovered);
        const here = allies.get(openWorldCoordinateKey(coordinate)) ?? [];
        const isSelected =
          selected !== null &&
          selected.rowIndex === rowIndex &&
          selected.columnIndex === columnIndex;
        return (
          <button
            key={openWorldQuadrantLabel(coordinate)}
            type="button"
            className={`open-world-map__cell is-${state}${isSelected ? ' is-selected' : ''}`}
            aria-label={cellLabel(coordinate, state, here)}
            aria-pressed={isSelected}
            onClick={() => onSelect(coordinate)}
          >
            <span className="open-world-map__cell-label" aria-hidden="true">
              {openWorldQuadrantLabel(coordinate)}
            </span>
            {/*
              A contagem, e nunca os nomes: um nome longo mudaria o tamanho da
              célula, e cem células de tamanhos diferentes deixam de ser uma
              grelha. Os nomes estão no rótulo acessível e no detalhe.
            */}
            {here.length > 0 ? (
              <span className="open-world-map__cell-allies" aria-hidden="true">
                {here.length}
              </span>
            ) : null}
          </button>
        );
      })}
    </>
  );
}
